import { useEffect, useRef } from 'react';

interface UseDialogOptions {
  isOpen: boolean;
  onClose: () => void;
  /**
   * Cand true (default), Tab/Shift+Tab face wrap in interiorul container-ului.
   * Pass false pentru role="menu" — Tab continua natural prin items, fara loop.
   */
  trapFocus?: boolean;
  /** Cand true (default), focus primul element focusable la deschidere. */
  autoFocus?: boolean;
  /** Element la care return focus-ul la inchidere. Default: previously-focused. */
  returnFocusTo?: HTMLElement | null;
}

const FOCUSABLE_SELECTOR =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Hook a11y pentru modale + popover-uri + meniuri:
 * - Esc handler -> onClose
 * - Focus trap (optional, default ON) -> Tab loop in interiorul container-ului
 * - Focus return -> la inchidere, focus revine la trigger-ul anterior
 * - Auto-focus primul focusable la deschidere (optional)
 *
 * Pattern WCAG 2.1.2 (No Keyboard Trap), 4.1.2 (Name/Role/Value), 2.4.3 (Focus Order).
 * Fix audit /facturi #10.
 *
 * Usage:
 *   const ref = useDialog({ isOpen, onClose });
 *   return <div ref={ref} role="dialog" aria-modal="true" aria-labelledby="...">...</div>;
 */
export function useDialog<T extends HTMLElement = HTMLDivElement>({
  isOpen,
  onClose,
  trapFocus = true,
  autoFocus = true,
  returnFocusTo,
}: UseDialogOptions) {
  const ref = useRef<T>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    previouslyFocused.current = document.activeElement as HTMLElement;

    if (autoFocus && ref.current) {
      const first = ref.current.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
      first?.focus();
    }

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key === 'Tab' && trapFocus && ref.current) {
        const focusables = ref.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      const target = returnFocusTo ?? previouslyFocused.current;
      if (target && document.contains(target)) target.focus();
    };
  }, [isOpen, onClose, trapFocus, autoFocus, returnFocusTo]);

  return ref;
}
